"use client";

import clsx from "clsx";
import type {TFunction} from "i18next";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {combinedApprovalStatus, resolveToolApprovalStatus, type ToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {useToolApprovalDecisionStates} from "@/contexts/chat/ToolApprovalDecisionContext";
import {useMutationProposalOutcomes} from "@/contexts/chat/MutationProposalOutcomesContext";
import {ReviewConversationContext, useMutationProposalReview, type MutationProposalReview} from "@/contexts/chat/MutationProposalReviewContext";
import {editsProposalTarget, mutationProposalHref, mutationProposalReviewParams, reviewEditorPage, type MutationProposal} from "@/services/wingman/mutation-proposal";
import {UnsavedDataContext} from "@/hooks/useHasUnsavedData";
import {useContext, useEffect, useRef, useState, type ReactNode} from "react";
import {displayToolPresentation, knownToolActivity} from "@repo/ui/gailgpt/tool-display";
import {isPresentationRecord, presentationText} from "@repo/ui/gailgpt/tool-presentation";
import {approvalPresentation, approvalStatusKey} from "./approvalPresentation";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import type {ApprovalBatch, ApprovalPart, ApprovalDecisionOutcome} from "@/types/chatMessages";
import {isApprovalActive} from "@/utils/chat/approvalParts";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import type {ToolApprovalResponse} from "@/services/wingman";
import ApprovalBatchActions from "./ApprovalBatchActions";
import ApprovalReviewScrollArea from "./ApprovalReviewScrollArea";
import MutationProposalReviewPanel from "./MutationProposalReviewPanel";
import ToolApprovalCard from "./ToolApprovalCard";
import {changedFields} from "./ScriptSnapshotDiff";
import ProposalFieldLabel from "./ProposalFieldLabel";
import {actionFieldLabel} from "./useCaseSnapshot";
import {proposalTitle, proposalTypeKey} from "./proposalIdentity";
import ApprovalDecisionStatus, {selectionClass} from "./ApprovalDecisionStatus";

/**
 * The editor already showing the target hosts its review in place, including a script revision.
 * Keeping the current path and query, whatever its ID's case, keeps the page and its draft mounted.
 */
function inPlaceReviewHref(pathname: string | null, search: string | undefined, proposal: MutationProposal, conversationId: string): string | undefined {
   const page = reviewEditorPage(pathname);
   if (!pathname || !page || !editsProposalTarget(page, proposal)) return;
   const query = new URLSearchParams(search);
   mutationProposalReviewParams(proposal, conversationId).forEach((value, key) => query.set(key, value));
   return `${pathname}?${query}`;
}

/** This proposal is the one the current page is reviewing. */
function proposalMatchesPageReview(proposal: MutationProposal, pageReview: MutationProposalReview, conversationId?: string): boolean {
   return proposal.proposalId === pageReview.proposalId && conversationId === pageReview.conversationId && editsProposalTarget(pageReview, proposal);
}

function proposalReviewHrefs(proposal: MutationProposal | undefined, conversationId: string | undefined, pathname: string | null, search: string | undefined) {
   const canonicalHref = proposal ? mutationProposalHref(proposal, conversationId) : undefined;
   const inPlaceHref = proposal && canonicalHref ? inPlaceReviewHref(pathname, search, proposal, conversationId!) : undefined;
   return {canonicalHref, inPlaceHref};
}

function approvalReviewTitle(parts: ApprovalPart[], t: TFunction<"ask">): string {
   if (parts.length !== 1) return t("toolApproval.proposals", {count: parts.length});
   const call = parts[0].data;
   if (call.approval.proposal) return call.approval.proposal.summary;
   const {presentation} = approvalPresentation(call);
   if (!call.approval.previewUnavailable && presentation) {
      return presentationText(displayToolPresentation(presentation, call.parameters, t).title, t);
   }
   return knownToolActivity(call.functionName, call.parameters, t)?.inProgressText ?? call.functionName;
}

interface ApprovalReviewProps {
   parts: ApprovalPart[];
   reviewParts?: ApprovalPart[];
   conversationId?: string;
   messageId?: string;
   approvalMessageIds?: Record<string, string>;
   activeMessageId?: string;
   disabled: boolean;
   onRespond?: (decisions: ToolApprovalResponse[]) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
   batch?: ApprovalBatch & {inline: boolean};
}

/** The chat owns live approval state; the shared panel only hosts its review UI. */
export default function ApprovalReview({parts, reviewParts = parts, conversationId, messageId, approvalMessageIds, activeMessageId, disabled, onRespond, onRequestFresh, batch}: ApprovalReviewProps) {
   const {t} = useTranslation("ask");
   const router = useRouter();
   const pathname = usePathname();
   const search = useSearchParams();
   const pageReview = useMutationProposalReview();
   const reportOutcome = useMutationProposalOutcomes()?.report;
   const reviewConversation = useContext(ReviewConversationContext);
   const shouldFinishReview = useRef(false);
   const {checkBeforeAction} = useContext(UnsavedDataContext);
   const {available: panelAvailable, activeArtifact, openArtifact, closeArtifact, approvalPanelHost, autoOpenedIds, coversConversation} = useArtifactPanel();
   const busy = useRef(false);
   const [pendingApprovalIds, setPendingApprovalIds] = useState<string[]>([]);
   const decisions = useToolApprovalDecisionStates(parts.map(part => part.data.approval.id));
   const pending = pendingApprovalIds.length > 0 || decisions.some(state => state.submitting) || !!batch?.submitting;
   const id = `approval:${parts[0].data.approval.id}`;
   const isOpen = activeArtifact?.kind === "approval" && activeArtifact.id === id;
   const title = approvalReviewTitle(parts, t);
   const isActive = (part: ApprovalPart) => isApprovalActive(part, {messageId, approvalMessageIds, activeMessageId});
   // A decision in flight is reported as submitting, so the page holds saves before the server answers.
   const statuses = parts.map((part, index) => resolveToolApprovalStatus({approval: part.data.approval,
      completed: part.isCompleted, active: isActive(part), result: part.result, pending: pendingApprovalIds.includes(part.data.approval.id) || decisions[index].submitting}));
   const status = combinedApprovalStatus(statuses, pending);
   const queued = parts.length === 1 && status === "required" && isActive(parts[0]) ? decisions[0].draft : null;

   const editorPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return !!proposal && !!mutationProposalHref(proposal, conversationId);
   });
   const preview = editorPart?.data.approval?.proposal;
   const summary = reviewSummary(parts, title, status);
   const {objectTitle} = summary;
   const {canonicalHref, inPlaceHref} = proposalReviewHrefs(preview, conversationId, pathname, search?.toString());
   const reviewInPanel = panelAvailable && !inPlaceHref && !pageReview;
   const matchingPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return pageReview != null && proposal != null && proposalMatchesPageReview(proposal, pageReview, conversationId);
   });
   const matchingStatus = matchingPart ? statuses[parts.indexOf(matchingPart)] : undefined;
   const matchingCapability = matchingPart?.data.approval?.proposal?.capability;
   const reviewable = reviewParts.flatMap(part => {
      const proposal = part.data.approval.proposal;
      return proposal && mutationProposalHref(proposal, conversationId) ? [proposal] : [];
   });
   const reviewIndex = reviewable.findIndex(proposal => proposal.proposalId === matchingPart?.data.approval.proposal?.proposalId);
   const navigateToReview = async ({canonicalHref, inPlaceHref}: ReturnType<typeof proposalReviewHrefs>) => {
      const href = inPlaceHref ?? canonicalHref;
      if (!href || (!inPlaceHref && !await checkBeforeAction())) return;
      // On a phone the conversation covers the editor being opened for review.
      reviewConversation?.showPage();
      router.push(href);
   };
   const openProposal = (proposal: MutationProposal) =>
      navigateToReview(proposalReviewHrefs(proposal, conversationId, pathname, search?.toString()));
   const setHasReview = pageReview?.setHasReview;
   useEffect(() => {
      if (!matchingPart || !setHasReview) return;
      setHasReview(true);
      return () => setHasReview(false);
   }, [matchingPart, setHasReview]);
   // Approved is still awaiting execution, so only a settled outcome releases the editor.
   const decisionPending = matchingStatus === "required" || matchingStatus === "submitting" || matchingStatus === "approved";
   const setDecisionPending = pageReview?.setDecisionPending;
   useEffect(() => {
      if (!matchingPart || !setDecisionPending) return;
      setDecisionPending(decisionPending);
      return () => setDecisionPending(true);
   }, [matchingPart, setDecisionPending, decisionPending]);
   // Editors stay mounted through a decision and may outlive this conversation, so the page
   // tracks every outcome: it holds their saves during execution and refreshes them after.
   useEffect(() => {
      if (!reportOutcome) return;
      parts.forEach((part, index) => {
         const {id: approvalId, proposal} = part.data.approval;
         if (proposal && mutationProposalHref(proposal, conversationId)) reportOutcome(approvalId, proposal, statuses[index]);
      });
   });
   const finish = pageReview?.finish;
   useEffect(() => {
      // An Action review stays open after a decision so its saved outcome can be inspected.
      if (reviewable.length <= 1 && matchingCapability !== "use_cases_update" && finish && shouldFinishReview.current && !disabled && (matchingStatus === "completed" || matchingStatus === "denied")) {
         // Consume this decision's automatic close so manually reopening stays open.
         shouldFinishReview.current = false;
         finish();
      }
   }, [matchingCapability, finish, disabled, matchingStatus, reviewable.length]);

   // A route change must not leave an orphaned proposal in the shared panel.
   useEffect(() => () => closeArtifact(id), [closeArtifact, id]);

   // Additional proposals can join this reply while its review is already open.
   useEffect(() => {
      if (isOpen && activeArtifact?.kind === "approval" && activeArtifact.title !== objectTitle) {
         openArtifact({...activeArtifact, title: objectTitle});
      }
   }, [isOpen, activeArtifact, objectTitle, openArtifact]);

   // Open the first active review once, including actions accepted before they render.
   // Remember dismissal across message regrouping and subsequent decision updates.
   const autoOpen = reviewInPanel && ["required", "approved", "completed"].includes(status) && isActive(parts[0]) && !!onRespond && !disabled
      && reviewParts.find(part => isActive(part))?.data.approval.id === parts[0].data.approval.id;
   useEffect(() => {
      if (!autoOpen || coversConversation || autoOpenedIds.has(id)) return;
      // Wait until mount effects settle so Strict Mode's cleanup cannot close
      // the panel immediately after marking this proposal as already opened.
      let canceled = false;
      queueMicrotask(() => {
         if (canceled || autoOpenedIds.has(id)) return;
         autoOpenedIds.add(id);
         if (!activeArtifact) openArtifact({kind: "approval", id, title: objectTitle});
      });
      return () => { canceled = true; };
   }, [autoOpen, coversConversation, autoOpenedIds, id, activeArtifact, openArtifact, objectTitle]);

   const respondToDecisions = async (decisions: ToolApprovalResponse[]): Promise<ApprovalDecisionOutcome> => {
      if (busy.current || disabled || !onRespond) return "ignored";
      shouldFinishReview.current = true;
      busy.current = true;
      setPendingApprovalIds(decisions.map(decision => decision.id));
      // The deciding surface reports a failed submission; the proposal stays open to retry.
      try {
         return await onRespond(decisions);
      } finally {
         busy.current = false;
         setPendingApprovalIds([]);
      }
   };

   const respond = (decision: ToolApprovalResponse) => respondToDecisions([decision]);
   const positionFor = (part: ApprovalPart) => {
      const index = batch?.pending.findIndex(item => item.data.approval.id === part.data.approval.id) ?? -1;
      return index >= 0 && batch ? {index: index + 1, total: batch.pending.length, remaining: batch.undecidedIds.length} : undefined;
   };
   const openReview = async () => {
      if (reviewInPanel) { autoOpenedIds.add(id); openArtifact({kind: "approval", id, title: objectTitle}); }
      else if (inPlaceHref || canonicalHref) await navigateToReview({canonicalHref, inPlaceHref});
      else openArtifact({kind: "approval", id, title});
   };
   const renderCard = (part: ApprovalPart, layout: {embedded?: boolean; panel?: boolean; collapseOnApproval?: boolean} = {}) => <ToolApprovalCard
      key={part.data.approval.id} {...layout} position={positionFor(part)}
      call={part.data} completed={part.isCompleted} result={part.result} active={isActive(part)}
      disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh} />;
   const batchActions = batch && onRespond ? <ApprovalBatchActions {...batch} disabled={disabled || pending} onRespond={respondToDecisions} /> : null;
   const inlineBatchActions = batch?.inline && batchActions ? <div className="my-3">{batchActions}</div> : null;
   // Surfaces without a side review retain the full inline action.
   if (parts.length === 1 && !parts[0].data.approval.proposal && !reviewInPanel) return <>{inlineBatchActions}{renderCard(parts[0])}</>;

   return <>
      {inlineBatchActions}
      <ApprovalReviewSummary summary={summary} queued={queued} status={status} current={!!matchingPart} isOpen={isOpen} onOpen={openReview}>
         {!panelAvailable && !matchingPart && !isOpen && parts.filter((_, index) => statuses[index] !== "completed" && statuses[index] !== "denied").map(part => renderCard(part, {embedded: true}))}
      </ApprovalReviewSummary>
      {matchingPart && pageReview?.host && createPortal(
         <MutationProposalReviewPanel key={matchingPart.data.approval.proposal?.proposalId} call={matchingPart.data} completed={matchingPart.isCompleted} result={matchingPart.result}
            navigation={reviewNavigation(reviewIndex, reviewable, openProposal)}
            position={positionFor(matchingPart)}
            active={isActive(matchingPart)} disabled={disabled || pending} onRespond={onRespond ? respond : undefined} onRequestFresh={onRequestFresh}
            onClose={pageReview.finish} />,
         pageReview.host)}
      {isOpen && approvalPanelHost && createPortal(
         <div className="flex h-full min-h-0 flex-col">
            {batch && onRespond && <div className="shrink-0 border-b border-neutral px-5 py-4"><ApprovalBatchActions {...batch} compact disabled={disabled || pending} onRespond={respondToDecisions} /></div>}
            <ApprovalReviewScrollArea enabled={parts.length > 1} label={t("toolApproval.title")}
               className={parts.length === 1 ? "min-h-0 flex-1" : "bg-100/40 px-4 py-1"}>
               {parts.map(part => renderCard(part, {panel: parts.length === 1, collapseOnApproval: parts.length > 1}))}
            </ApprovalReviewScrollArea>
         </div>, approvalPanelHost)}
   </>;
}

function reviewSummary(parts: ApprovalPart[], title: string, status: ToolApprovalStatus) {
   const summaryProposal = parts.length === 1 ? parts[0].data.approval.proposal : undefined;
   const parameters = parts[0].data.parameters;
   const body = isPresentationRecord(parameters.body) ? parameters.body : parameters;
   const subject = parts.length === 1 ? [body.title, body.name].find((value): value is string => typeof value === "string" && !!value.trim()) : undefined;
   const objectTitle = summaryProposal ? proposalTitle(summaryProposal) : subject ?? title;
   const statusKey = parts.length === 1 ? approvalStatusKey(parts[0].data, status) : `toolApproval.${status}`;
   const fields = summaryProposal ? changedFields(summaryProposal) : [];

   return {summaryProposal, objectTitle, statusKey, fields};
}

function reviewNavigation(index: number, proposals: MutationProposal[], openProposal: (proposal: MutationProposal) => Promise<void>) {
   if (index < 0 || proposals.length <= 1) return undefined;
   return {
      index: index + 1, total: proposals.length,
      previous: index > 0 ? () => void openProposal(proposals[index - 1]) : undefined,
      next: index < proposals.length - 1 ? () => void openProposal(proposals[index + 1]) : undefined,
   };
}

function ApprovalReviewSummary({summary, queued, status, current, isOpen, onOpen, children}: {
   summary: ReturnType<typeof reviewSummary>;
   queued: boolean | null;
   status: ToolApprovalStatus;
   current: boolean;
   isOpen: boolean;
   onOpen: () => Promise<void>;
   children: ReactNode;
}) {
   const {t} = useTranslation("ask");
   const {summaryProposal, objectTitle, statusKey, fields} = summary;
   return <section aria-label={t("toolApproval.title")} className={clsx("my-3 overflow-clip rounded-xl border", selectionClass(queued) ?? "border-neutral bg-panel", (current || isOpen) && "ring-1 ring-primary/50")}>
      <button type="button" aria-expanded={current || isOpen}
         aria-current={current || isOpen ? "true" : undefined}
         onClick={onOpen}
         className="group block w-full rounded-xl text-left transition-colors hover:bg-100/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
         <span className="flex items-start gap-3 px-3.5 py-3">
            <Icon icon={summaryProposal?.capability === "use_cases_update" ? "list-check" : "file-lines"} className="mt-1 h-4 w-4 shrink-0 text-500" />
            <span className="min-w-0 flex-1">
               <span className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-500">
                  {summaryProposal && <span>{t(proposalTypeKey(summaryProposal))}</span>}
                  {current && <span className="font-semibold text-primary">{t("toolApproval.currentReview")}</span>}
                  <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
               </span>
               <span className="block break-words text-sm font-semibold leading-5 text-800">{objectTitle}</span>
               {fields.length > 0 && <span className="mt-1 block text-xs leading-5 text-500">
                  {fields.slice(0, 3).map((field, index) => <span key={field}>
                     {index > 0 && <span aria-hidden> · </span>}
                     <ProposalFieldLabel field={summaryProposal?.capability === "use_cases_update" ? actionFieldLabel(field) : field} />
                  </span>)}
                  {fields.length > 3 && <span> +{fields.length - 3}</span>}
               </span>}
               <span className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-primary">
                  {t(status === "completed" ? "toolApproval.viewChanges" : "toolApproval.review")}
                  <Icon icon="arrow-right" className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
               </span>
            </span>
         </span>
      </button>
      {children}
   </section>;
}
